import type { Region } from '~~/core/entities/Region';

import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import { getRegionMetadata } from './utils';

const regionName = 'Jardim Europa';

const metaTags = getRegionMetadata(regionName, 'São Paulo', { gender: 'male' });

const region: Region = {
	name: regionName,
	state: 'sp',
	city: 'São Paulo',
	path: '/bairros/jardim-europa',
	image: '/images/regions/jardim-europa.webp',
	metaTags,
	info: {
		title: '',
		description: ''
	},
	hero: {
		title: regionName,
		description:
			'Mergulhe na sofisticação de um dos bairros mais cobiçados de São Paulo.',
		backgroundImage: '/images/regions/jardim-europa/banner.webp'
	},
	map: {
		title: 'Jardim Europa: viva bem ao lado do centro econômico de São Paulo',
		content: `Localizado entre duas das principais avenidas de São Paulo e sendo cortado pela Faria Lima, polo de negócios mais importante da capital paulista, o Jardim Europa é perfeito para quem quer aliar uma ótima localização com luxo e sofisticação que apenas um bairro de casas pode entregar.<br/><br/>Apesar de estar ao lado da Faria Lima, o silêncio e calmaria do Jardim Europa surpreende os moradores que elegeram as ruas tranquilas para chamar de lar. Mas não pense que isso significa monotonia, pelo contrário: o bairro é recheado de opções de lazer, como clubes, cultura, como museus e até gastronomia. A seguir, apresentamos o que torna este bairro da zona sul de São Paulo um dos mais especiais para se viver.`,
		image: '/images/regions/jardim-europa/map.webp',
		links: [
			{
				label: 'Ver imóveis no Jardim Europa',
				url: PropertyFiltersPresenter.getPathFromFilters({
					propertyTypes: [],
					regions: [regionName],
					states: ['SP'],
					cities: ['São Paulo']
				})
			}
		]
	},
	content: [
		{
			info: {
				title: `Restaurantes do ${regionName}`,
				content:
					'Se, de um lado da Faria Lima, o Jardim Europa é exclusivamente um bairro de casas, do outro, não faltam opções para aproveitar. Se você procura lazer completo e ótimos restaurantes, o tradicional Shopping Iguatemi é uma ótima pedida. Localizado no meio do bairro, o shopping conta com opções que vão de clássicos paulistanos, como o Ritz, com pratos clássicos como milanesa e picadinho, o Rodeio e seus ótimos cortes de carne na brasa e o Bottega Bernacca com uma cozinha italiana de ponta, até a alta gastronomia oriental do Aima.<br><br>Localizado no jardim do Museu da Casa Brasileira, o Capim Santo oferece uma gastronomia brasileira contemporânea com pratos deliciosos. Perfeito para um almoço de sábado com a família.<br><br>A pizzaria Cristal, um clássico de São Paulo, é perfeita para toda a família. Com salão amplo e opções deliciosas no cardápio, ela recebe encontros aos fins de semana e reúne amigos de longa data. O delivery, que entrega rapidíssimo no Jardim Europa, também é uma pedida para os dias de preguiça.'
			},
			images: [
				{
					url: '/images/regions/jardim-europa/content/content-1.webp'
				},
				{
					url: '/images/regions/jardim-europa/content/content-2.webp',
					credits: 'Klara Kulikova',
					class: 'col-start-2'
				}
			]
		},
		{
			info: {
				title: `Os clubes do ${regionName}`,
				content:
					'Se você procura um lugar para praticar esportes, conhecer outros moradores, curtir os finais de semana na beira da piscina e ainda garantir um lugar seguro e divertido para as crianças, o Jardim Europa conta com dois clubes especiais e localizados no coração do bairro.<br><br>Um dos mais tradicionais de São Paulo, o Esporte Clube Pinheiros está localizado na Av. Faria Lima, ao lado do Shopping Iguatemi. É conhecido por sua vasta gama de esportes e forma alguns dos principais atletas do país.<br><br>Ali perto, na Rua Hungria, o Clube Hebraica reúne 22 mil sócios e conta com escola maternal e infantil, além de uma série de atividades voltadas para empreendedores, de co-working à cursos profissionalizantes. Opções de esportes e lazer também não faltam.',
				class: 'lg:col-start-2'
			},
			images: [
				{
					url: '/images/regions/jardim-europa/content/content-3.webp'
				}
			]
		},
		{
			info: {
				title: `Parques no ${regionName}`,
				content:
					'Apesar do Jardim Europa estar localizado próximo ao Parque do Povo, é necessário pegar o carro ou uma bike para acessá-lo, mas, se você quer uma área verde próxima ao lar, a Praça Morungaba é a pedida: com playground e área para cães, ela é uma das favoritas dos moradores.<br><br>Perto dali, o Museu da Casa Brasileira conta com um imenso jardim arborizado, perfeito para relaxar e encontrar um refúgio no meio da Avenida Faria Lima. Não deixe de aproveitar o restaurante Capim Santo.'
			},
			images: [
				{
					url: '/images/regions/jardim-europa/content/content-4.webp',
					class: 'row-start-2'
				},
				{
					url: '/images/regions/jardim-europa/content/content-5.webp',
					class: 'row-start-2'
				}
			]
		},
		{
			info: {
				title: `Imóvel icônico no ${regionName}`,
				content:
					'Já se imaginou vivendo em um imóvel histórico na capital paulista? Na Casa Amarela, localizada no Jardim Europa, isso é possível. Famosa por ter sido a residência de Assis Chateaubriand, o Chatô, durante anos. Construída nos anos 20, a casa tem estilo colonial português e foi inteirinha renovada, contando, atualmente, com elevadores, ar condicionado e um equipamento de segurança de última geração. Com cinco suítes, oito banheiros e nove vagas de garagem, a casa é ampla e ainda conta com espaço gourmet, cinema, piscina aquecida e até uma pequena praça.',
				class: 'lg:col-start-2'
			},
			images: [
				{
					url: '/images/regions/jardim-europa/content/content-6.webp',
					credits: 'LA164'
				},
				{
					url: '/images/regions/jardim-europa/content/content-7.webp',
					credits: 'LA164',
					class: 'row-start-2 col-start-2'
				}
			]
		}
	],
	nearRegions: [
		{
			image: '/images/regions/itaim-bibi/banner.webp',
			name: 'Itaim Bibi',
			href: '/bairros/itaim-bibi'
		},
		{
			image: '/images/regions/jardim-paulista/banner.webp',
			name: 'Jardim Paulista',
			href: '/bairros/jardim-paulista'
		},
		{
			image: '/images/regions/vila-nova-conceicao/banner.webp',
			name: 'Vila Nova Conceição',
			href: '/bairros/vila-nova-conceicao'
		},
		{
			image: '/images/regions/jardim-america/banner.webp',
			name: 'Jardim América',
			href: '/bairros/jardim-america'
		},
		{
			image: '/images/regions/pinheiros/banner.webp',
			name: 'Pinheiros',
			href: '/bairros/pinheiros'
		},
		{
			image: '/images/regions/alto-de-pinheiros/banner.webp',
			name: 'Alto de Pinheiros',
			href: '/bairros/alto-de-pinheiros'
		},
		{
			image: '/images/regions/jardim-paulistano/banner.webp',
			name: 'Jardim Paulistano',
			href: '/bairros/jardim-paulistano'
		}
	],
	questions: [
		{
			question: `Onde fica o ${regionName}?`,
			answer:
				'O Jardim Europa está localizado na cidade de São Paulo, na Zona Sul, dentro do distrito de Pinheiros e administrado pela Subprefeitura de mesmo nome. O bairro é cercado por alguns dos mais exclusivos bairros da cidade, como o Jardim América e o Itaim Bibi. As principais avenidas do bairro incluem a Avenida Faria Lima, que corta o bairro, e a Avenida Brasil, localizando o Jardim Europa entre duas das avenidas mais importantes de São Paulo.'
		},
		{
			question: `Como chegar ao ${regionName}?`,
			answer:
				'O Jardim Europa é de fácil acesso, especialmente para quem utiliza transporte privado. O bairro é cortado pela Avenida Faria Lima e está localizado próximo à Avenida Brasil, duas das vias mais importantes de São Paulo, que conectam o Jardim Europa à região central e outras áreas da cidade. A mobilidade no bairro é facilitada também pela proximidade com a Marginal Pinheiros, permitindo rápido acesso de carro. Para quem utiliza transporte público, há estações de metrô e diversas linhas de ônibus nas proximidades, mas a área é mais conhecida por ser acessível por carro, especialmente para quem vem do Itaim Bibi ou do Jardim América.'
		},
		{
			question: `O que fazer no ${regionName}?`,
			answer:
				'O Jardim Europa oferece diversas opções de lazer e cultura. Durante o dia, o Shopping Iguatemi é um dos principais pontos de interesse, com uma vasta gama de lojas de luxo, serviços e opções de entretenimento. O shopping é ideal para quem busca um passeio de compras, com marcas renomadas e exclusivas. Para quem deseja relaxar ao ar livre, o Museu da Casa Brasileira conta com um belíssimo jardim arborizado, perfeito para momentos tranquilos. O bairro também possui a Praça Morungaba, um local agradável para passeios com animais de estimação ou um momento de lazer. Além disso, os clubes do bairro, como o Esporte Clube Pinheiros e o Clube Hebraica, oferecem uma infraestrutura completa para esportes e atividades sociais.'
		},
		{
			question: `Onde comer no ${regionName}?`,
			answer:
				'O Jardim Europa oferece uma excelente variedade gastronômica. O Shopping Iguatemi abriga restaurantes renomados, como o Ritz, especializado em pratos clássicos paulistanos, o Rodeio, famoso por suas carnes, e o Aima, que serve alta gastronomia oriental. Para um almoço descontraído, o Capim Santo, localizado no jardim do Museu da Casa Brasileira, é a pedida, com sua cozinha brasileira contemporânea. Se a opção for uma pizza, a Pizzaria Cristal é uma das favoritas no bairro, oferecendo um cardápio variado e ambiente acolhedor.'
		}
	],
	seo: {
		title: `Quem se interessou por ${regionName} também procurou por`,
		links: [
			{
				title: 'Tipo de imóvel',
				links: [
					{
						label: 'Casa',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.HOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Apartamento',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.APARTMENT],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Casa em condomínio',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.CONDO_HOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					}
				]
			},
			{
				title: ' ',
				links: [
					{
						label: 'Cobertura',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.PENTHOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Terreno',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.LAND],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Duplex',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.DUPLEX],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					}
				]
			},
			{
				title: 'Localização',
				links: [
					{
						label: 'Jardim Paulista, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Jardim Paulista']
						})
					},
					{
						label: 'Vila Nova Conceição, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Vila Nova Conceição']
						})
					},
					{
						label: 'Jardim América, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Jardim América']
						})
					}
				]
			},
			{
				title: ' ',
				links: [
					{
						label: 'Higienópolis, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Higienópolis']
						})
					},
					{
						label: 'Brooklin, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Brooklin']
						})
					},
					{
						label: 'Moema, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Moema']
						})
					}
				]
			}
		],
		discoveryContent: {
			heading: `Mais sobre o ${regionName}`,
			paragraphs: [
				'O Jardim Europa é um dos bairros mais cobiçados de São Paulo, situado entre duas das principais avenidas da cidade e cortado pela Faria Lima. Esse bairro combina uma excelente localização com luxo e sofisticação, sendo um verdadeiro refúgio na agitação da capital. Repleto de opções de lazer, cultura e gastronomia, o Jardim Europa se destaca como um lugar especial para viver.',
				'O bairro abriga renomados restaurantes, como Ritz e Aima, e o Capim Santo, no jardim do Museu da Casa Brasileira, que oferece uma experiência gastronômica contemporânea. Para esportes e socialização, o bairro abriga clubes de prestígio, como o Esporte Clube Pinheiros e o Clube Hebraica. Entre os parques, a Praça Morungaba e o extenso jardim do Museu da Casa Brasileira são ótimas opções. Os imóveis variam desde a histórica Casa Amarela, residência de Assis Chateaubriand, até luxuosos edifícios como o Edifício Buritama e o Condomínio Avignon, exemplos claros do alto padrão que caracteriza o bairro, com ambientes que refletem a sofisticação e o estilo de vida dos moradores do bairro.'
			]
		}
	}
};

export default region;
